import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  Input,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { EtJournalESeal, EtJournalSigner } from '@et/typings';
import { finalize } from 'rxjs';
import { EjournalService } from '../../services/ejournal.service';
import {
  JournalSealAddEditComponent,
  JournalSealAddEditComponentData,
} from '../journal-seal-add-edit/journal-seal-add-edit.component';
import { PreviewPdfBtnComponent } from '../preview-pdf-btn/preview-pdf-btn.component';
import { SealTagComponent } from '../seal-tag/seal-tag.component';

@Component({
  selector: 'et-journal-seal-card',
  imports: [
    DatePipe,
    CurrencyPipe,
    MatDialogClose,
    PresentationalAtomsModule,
    PreviewPdfBtnComponent,
    SealTagComponent,
  ],
  templateUrl: './journal-seal-card.component.html',
  styleUrl: './journal-seal-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalSealCardComponent {
  @Input({ required: true }) seal!: EtJournalESeal;
  @Input({ required: true }) signers!: EtJournalSigner[];

  @ViewChild('deleteSealModal') deleteSealModal!: TemplateRef<unknown>;

  private ejournalService = inject(EjournalService);
  private dialog = inject(MatDialog);
  private injector = inject(Injector);

  isDeletingSeal = signal(false);
  isUpdatingSigners = signal(false);

  get sourceLabel(): string {
    if (this.seal.source === 'eSeal') {
      return 'eSeal';
    }
    if (this.seal.source === 'deletedESeal') {
      return 'Deleted eSeal';
    }
    return 'Seal';
  }

  /** Clear for audit-origin cards; Delete for manual seals and deleted eSeals */
  get isClearAction(): boolean {
    return this.seal.source === 'eSeal';
  }

  isSignerSelected(signerId: string): boolean {
    return this.seal.signerIds.includes(signerId);
  }

  onEditSeal(): void {
    this.dialog.open<
      JournalSealAddEditComponent,
      JournalSealAddEditComponentData
    >(JournalSealAddEditComponent, {
      injector: this.injector,
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      width: '100%',
      maxWidth: '800px',
      maxHeight: '90vh',
      data: { seal: this.seal },
    });
  }

  onDeleteSeal(): void {
    this.dialog.open(this.deleteSealModal, {
      width: '100%',
      maxWidth: '500px',
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      id: 'delete-seal-modal',
    });
  }

  deleteSeal(): void {
    this.isDeletingSeal.set(true);
    this.ejournalService
      .deleteSeal(this.seal)
      .pipe(finalize(() => this.isDeletingSeal.set(false)))
      .subscribe(() => {
        this.dialog.getDialogById('delete-seal-modal')?.close();
      });
  }

  onToggleSignerSelection(signerId: string): void {
    if (this.isUpdatingSigners()) {
      return;
    }

    const isSelected = this.seal.signerIds.includes(signerId);
    const signerIds = isSelected
      ? this.seal.signerIds.filter((id) => id !== signerId)
      : [...this.seal.signerIds, signerId];

    this.isUpdatingSigners.set(true);
    this.ejournalService
      .saveSeal({ ...this.seal, signerIds })
      .pipe(finalize(() => this.isUpdatingSigners.set(false)))
      .subscribe();
  }
}
